import { useState } from "react";
import {
  Lock,
  CheckCircle,
  Eye,
  EyeOff,
} from "lucide-react";
import { resetPassword } from "../../lib/api/auth";
import { useAuth } from "../../app/AuthContext";
import { supabase } from "../../lib/supabase";
import LogoCleanRebg from "../../assets/images/logo-clean-removebg.png";
import LogoClean from "../../assets/images/logo-clean.png";

interface FormErrors {
  password?: string;
  confirmPassword?: string;
}

interface ResetPasswordPageProps {
  onLoginClick?: () => void;
  onSuccess?: () => void;
}

export default function ResetPasswordPage({
  onLoginClick,
  onSuccess,
}: ResetPasswordPageProps) {
  const [password, setPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { isAuthenticated, logout } = useAuth(); // Destructure logout

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!password) {
      newErrors.password = "Nova senha é obrigatória";
    } else if (password.length < 6) {
      newErrors.password = "A senha deve ter pelo menos 6 caracteres";
    }

    if (password !== confirmPassword) {
      newErrors.confirmPassword = "As senhas não coincidem";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (validateForm()) {
      setIsLoading(true);
      try {
        console.log("Iniciando handleSubmit em ResetPassword...");

        // Check the current session
        let {
          data: { session },
        } = await supabase.auth.getSession();
        console.log("Sessão atual detectada:", session ? "Sim" : "Não");

        // If no session exists, try to force it from the hash
        const hash = window.location.hash;
        if (!session && hash.includes("access_token=")) {
          console.log(
            "Sessão ausente, mas hash detectado. Tentando setSession...",
          );
          const params = new URLSearchParams(hash.replace("#", "?"));
          const accessToken = params.get("access_token");
          if (accessToken) {
            const { data: sessionData, error: sessionError } =
              await supabase.auth.setSession({
                access_token: accessToken,
                refresh_token: params.get("refresh_token") || "",
              });

            if (sessionError) {
              console.error("Erro ao setar sessão via hash:", sessionError);
              throw sessionError;
            }

            session = sessionData.session;
            console.log(
              "Sessão definida com sucesso via hash:",
              session ? "Sim" : "Não",
            );
          }
        }

        if (!session) {
          console.error(
            "Nenhuma sessão ativa encontrada após todas as tentativas.",
          );
          throw new Error(
            "Sessão de autenticação ausente! Por favor, use o link enviado para o seu e-mail.",
          );
        }

        console.log("Chamando supabase.auth.updateUser...");
        const { error: updateError } = await supabase.auth.updateUser({
          password: password,
        });

        if (updateError) {
          console.error("Erro no updateUser:", updateError);
          throw updateError;
        }

        console.log("Senha redefinida com sucesso!");
        setIsSuccess(true);
      } catch (error: any) {
        console.error("Erro ao redefinir senha:", error);
        const message =
          error.message ||
          "Verifique se o link ainda é válido ou se já expirou.";
        setErrors({ password: `Erro ao redefinir senha: ${message}` });
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleSuccessClick = async () => {
    logout();
    await supabase.auth.signOut();
    if (onSuccess) {
      onSuccess();
    }
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-(--bg-primary) flex flex-col">
        {/* Navigation */}
        <nav className="fixed top-0 w-full bg-(--header-bg) shadow-sm z-50 border-b border-(--border-color)">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <div className="flex items-center gap-3">
                <img
                  src={LogoCleanRebg}
                  alt="Logo"
                  className="h-15 sm:h-20 w-auto object-contain cursor-pointer"
                  onClick={() => (window.location.href = "/")}
                />
                <span className="text-base sm:text-xl font-semibold text-(--text-secondary)">
                  SIGA - Sistema
                </span>
              </div>
            </div>
          </div>
        </nav>

        <main className="grow flex items-center justify-center p-4 pt-24 pb-12">
          <div className="w-full max-w-md lg:max-w-5xl">
            <div
              className="bg-(--card-bg) rounded-2xl shadow-lg border lg:flex lg:overflow-hidden"
              style={{ borderColor: "var(--border-color)" }}
            >
              <div
                className="hidden lg:flex lg:w-5/12 lg:flex-col lg:items-center lg:justify-center lg:p-12"
                style={{ backgroundColor: "var(--bg-secondary)" }}
              >
                <div
                  className="inline-flex items-center justify-center w-24 h-24 rounded-full mb-6"
                  style={{ backgroundColor: "rgba(24, 128, 56, 0.1)" }}
                >
                  <CheckCircle
                    className="w-20 h-20"
                    style={{ color: "var(--success-color)" }}
                  />
                </div>
                <h2
                  className="text-2xl font-bold mb-2"
                  style={{ color: "var(--text-primary)" }}
                >
                  Senha Alterada
                </h2>
                <p
                  className="text-center text-base"
                  style={{ color: "var(--text-tertiary)" }}
                >
                  Sua senha foi redefinida com sucesso
                </p>
              </div>

              <div className="p-8 lg:w-7/12 lg:p-12 text-center lg:text-left">
                <div
                  className="lg:hidden inline-flex items-center justify-center w-16 h-16 rounded-full mb-4"
                  style={{ backgroundColor: "rgba(24, 128, 56, 0.1)" }}
                >
                  <CheckCircle
                    className="w-12 h-12"
                    style={{ color: "var(--success-color)" }}
                  />
                </div>

                <h1
                  className="text-3xl font-bold mb-4"
                  style={{ color: "var(--text-primary)" }}
                >
                  Tudo pronto!
                </h1>

                <p
                  className="text-base mb-8"
                  style={{ color: "var(--text-tertiary)" }}
                >
                  Sua senha foi atualizada. Agora você já pode acessar sua conta
                  normalmente com a nova senha.
                </p>

                <button
                  onClick={handleSuccessClick}
                  className="w-full text-white py-3 px-4 rounded-lg font-semibold hover:shadow-lg transition-all shadow-md"
                  style={{ backgroundColor: "var(--primary-color)" }}
                >
                  Ir para o Login
                </button>
              </div>
            </div>
          </div>
        </main>

        <footer
          className="text-white py-12 px-4"
          style={{ backgroundColor: "#202124" }}
        >
          <div className="max-w-7xl mx-auto">
            <div className="grid md:grid-cols-4 gap-8 mb-8">
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <div>
                    <img src={LogoClean} alt="Logo" className="w-8 h-8 " />
                  </div>
                  <span className="text-lg font-semibold">
                    SIGA - Sistema Inteligente de Gestão Acadêmica
                  </span>
                </div>
                <p className="text-sm" style={{ color: "#9aa0a6" }}>
                  Educação impulsionada por inteligência artificial
                </p>
              </div>
            </div>

            <div className="border-t pt-8 text-center text-sm border-(--border-color)">
              <p>
                © {new Date().getFullYear()} SIGA. Todos os direitos reservados.
              </p>
            </div>
          </div>
        </footer>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-(--bg-primary) flex flex-col">
      <nav className="fixed top-0 w-full bg-(--header-bg) shadow-sm z-50 border-b border-(--border-color)">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <img
                src={LogoCleanRebg}
                alt="Logo"
                className="h-15 sm:h-20 w-auto object-contain cursor-pointer"
                onClick={() => (window.location.href = "/")}
              />
              <span className="text-base sm:text-xl font-semibold text-(--text-secondary)">
                SIGA - Sistema Inteligente de Gestão Acadêmica
              </span>
            </div>
          </div>
        </div>
      </nav>

      <main className="grow flex items-center justify-center p-4 pt-24 pb-12">
        <div className="w-full max-w-md lg:max-w-5xl">
          <div
            className="bg-(--card-bg) rounded-2xl shadow-lg border lg:flex lg:overflow-hidden"
            style={{ borderColor: "var(--border-color)" }}
          >
            <div
              className="hidden lg:flex lg:w-5/12 lg:flex-col lg:items-center lg:justify-center lg:p-12"
              style={{ backgroundColor: "var(--bg-secondary)" }}
            >
              <div
                className="inline-flex items-center justify-center w-24 h-24 rounded-full mb-6"
                style={{ backgroundColor: "var(--primary-color)" }}
              >
                <div className="w-20 h-20 rounded-lg flex items-center justify-center">
                  <Lock className="w-14 h-14 text-white" />
                </div>
              </div>
              <h2
                className="text-2xl font-bold mb-2"
                style={{ color: "var(--text-primary)" }}
              >
                Nova Senha
              </h2>
              <p
                className="text-center text-base"
                style={{ color: "var(--text-tertiary)" }}
              >
                Crie uma senha forte e segura
              </p>
            </div>

            <div className="p-8 lg:w-7/12 lg:p-12">
              <div className="text-center mb-8 lg:text-left">
                <h1
                  className="text-3xl font-bold mb-2"
                  style={{ color: "var(--text-primary)" }}
                >
                  Redefinir Senha
                </h1>
                <p
                  className="text-base"
                  style={{ color: "var(--text-tertiary)" }}
                >
                  Escolha uma nova senha para sua conta.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label
                    htmlFor="password"
                    className="block text-sm font-medium mb-2"
                    style={{ color: "var(--text-primary)" }}
                  >
                    Nova Senha
                  </label>
                  <div className="relative">
                    <input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="block w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition disabled:opacity-50 placeholder-(--text-tertiary)"
                      style={{
                        borderColor: errors.password
                          ? "var(--danger-color)"
                          : "var(--border-color)",
                        color: "var(--text-primary)",
                        backgroundColor: "var(--input-bg)",
                      }}
                      placeholder="••••••••"
                      disabled={isLoading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      style={{ color: "var(--text-tertiary)" }}
                    >
                      {showPassword ? (
                        <EyeOff className="h-5 w-5" />
                      ) : (
                        <Eye className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                  {errors.password && (
                    <p
                      className="mt-1 text-sm"
                      style={{ color: "var(--danger-color)" }}
                    >
                      {errors.password}
                    </p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="confirmPassword"
                    className="block text-sm font-medium mb-2"
                    style={{ color: "var(--text-primary)" }}
                  >
                    Confirmar Nova Senha
                  </label>
                  <div className="relative">
                    <input
                      id="confirmPassword"
                      type={showPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="block w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition disabled:opacity-50 placeholder-(--text-tertiary)"
                      style={{
                        borderColor: errors.confirmPassword
                          ? "var(--danger-color)"
                          : "var(--border-color)",
                        color: "var(--text-primary)",
                        backgroundColor: "var(--input-bg)",
                      }}
                      placeholder="••••••••"
                      disabled={isLoading}
                    />
                  </div>
                  {errors.confirmPassword && (
                    <p
                      className="mt-1 text-sm"
                      style={{ color: "var(--danger-color)" }}
                    >
                      {errors.confirmPassword}
                    </p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full text-white py-3 px-4 rounded-lg font-semibold hover:shadow-lg transition-all shadow-md disabled:opacity-50 mt-4"
                  style={{ backgroundColor: "var(--primary-color)" }}
                >
                  {isLoading ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Atualizando...
                    </span>
                  ) : (
                    "Redefinir Senha"
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </main>

      <footer
        className="text-white py-12 px-4"
        style={{ backgroundColor: "#202124" }}
      >
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div>
                  <img src={LogoClean} alt="Logo" className="w-8 h-8 " />
                </div>
                <span className="text-lg font-semibold">SIGA</span>
              </div>
              <p className="text-sm" style={{ color: "#9aa0a6" }}>
                Educação impulsionada por inteligência artificial
              </p>
            </div>
          </div>

          <div className="border-t pt-8 text-center text-sm border-(--border-color)">
            <p>
              © {new Date().getFullYear()} SIGA. Todos os direitos reservados.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
